import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { auth } from "../../api";
import { ROLE_HOME } from "../../components/ProtectedRoute";
import toast from "react-hot-toast";

export default function ForceChangePasswordPage() {
  const { user, clearMustChange, logout } = useAuth();
  const navigate = useNavigate();

  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading,         setLoading]         = useState(false);
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);

  const strength = getStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await auth.forceChangePassword({
        new_password:     newPassword,
        confirm_password: confirmPassword,
      });
      toast.success("Password updated! Please log in again.");
      clearMustChange();
      // Must re-login because tokens are now invalid after password change
      logout();
      navigate("/login", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.new_password?.[0]
        || err.response?.data?.detail
        || "Failed to update password.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.grid} />
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <span style={styles.icon}>🔐</span>
        </div>

        <h1 style={styles.title}>Set your password</h1>
        <p style={styles.subtitle}>
          Hi {user?.name?.split(" ")[0] || "there"}! Your account was created with a temporary
          password. Please set a new password to continue.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>New password</label>
            <div style={styles.pwWrap}>
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
                autoFocus
                style={{ ...styles.input, paddingRight: "44px" }}
              />
              <button type="button" onClick={() => setShowNew(p => !p)} style={styles.eyeBtn}>
                {showNew ? "🙈" : "👁️"}
              </button>
            </div>
            {newPassword && (
              <div style={styles.strengthBar}>
                <div style={{
                  ...styles.strengthFill,
                  width: `${strength.pct}%`,
                  background: strength.color,
                }} />
              </div>
            )}
            {newPassword && (
              <span style={{ ...styles.strengthLabel, color: strength.color }}>
                {strength.label}
              </span>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Confirm password</label>
            <div style={styles.pwWrap}>
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                required
                style={{
                  ...styles.input,
                  paddingRight: "44px",
                  borderColor: confirmPassword && confirmPassword !== newPassword
                    ? "#EF4444"
                    : confirmPassword && confirmPassword === newPassword
                    ? "#22C55E"
                    : undefined,
                }}
              />
              <button type="button" onClick={() => setShowConfirm(p => !p)} style={styles.eyeBtn}>
                {showConfirm ? "🙈" : "👁️"}
              </button>
            </div>
            {confirmPassword && confirmPassword !== newPassword && (
              <span style={{ fontSize: "12px", color: "#EF4444" }}>Passwords don't match</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            style={{
              ...styles.btn,
              opacity: (loading || !newPassword || !confirmPassword || newPassword !== confirmPassword) ? 0.5 : 1,
            }}
          >
            {loading ? "Updating…" : "Set password & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

function getStrength(pw) {
  if (!pw) return { pct: 0, color: "var(--text-faint)", label: "" };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { pct: 25,  color: "#EF4444", label: "Weak" };
  if (score <= 2) return { pct: 50,  color: "#F97316", label: "Fair" };
  if (score <= 3) return { pct: 75,  color: "#EAB308", label: "Good" };
  return              { pct: 100, color: "#22C55E", label: "Strong" };
}

const styles = {
  root: {
    minHeight: "100vh",
    background: "#0A0F1E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  grid: {
    position: "absolute",
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)
    `,
    backgroundSize: "48px 48px",
    maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
  },
  card: {
    position: "relative",
    background: "var(--surface-bg)",
    border: "1px solid rgba(37,99,235,0.25)",
    borderRadius: "20px",
    padding: "48px 44px",
    width: "100%",
    maxWidth: "440px",
    boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
    backdropFilter: "blur(20px)",
  },
  iconWrap: {
    marginBottom: "20px",
  },
  icon: {
    fontSize: "36px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#fff",
    margin: "0 0 8px",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "14px",
    color: "var(--text-secondary)",
    margin: "0 0 28px",
    lineHeight: "1.6",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "500",
    color: "var(--text-secondary)",
  },
  pwWrap: { position: "relative" },
  input: {
    background: "var(--input-bg)",
    border: "1px solid var(--input-border)",
    borderRadius: "10px",
    padding: "12px 14px",
    color: "var(--text-primary)",
    fontSize: "15px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.6,
    padding: "4px",
  },
  strengthBar: {
    height: "3px",
    background: "var(--input-border)",
    borderRadius: "2px",
    overflow: "hidden",
  },
  strengthFill: {
    height: "100%",
    borderRadius: "2px",
    transition: "width 0.3s, background 0.3s",
  },
  strengthLabel: {
    fontSize: "11px",
    fontWeight: "500",
  },
  btn: {
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "13px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "4px",
    transition: "opacity 0.2s",
  },
};
